<template>
  <div>
    <div class="topleft">
      <v-select
        v-model="benefitType"
        item-value="value"
        return-object
        item-text="text"
        hide-details
        :items="allBenefitTypes"
        class="pt-0"
      >
      </v-select>
    </div>
    <div class="topright">
      <v-radio-group v-model="against" row class="pt-0 mt-1" hide-details>
        <v-radio label="Revenue" value="revenue"></v-radio>
        <v-radio label="Profit" value="profit"></v-radio>
      </v-radio-group>
    </div>
    <ECharts
      ref="echartInstance"
      :options="options"
      auto-resize
      :style="{ height: height }"
      watchShallow
    ></ECharts>
  </div>
</template>
<script>
/* eslint-disable import/extensions, import/no-unresolved */

import Vue from 'vue';
import ECharts from 'vue-echarts/components/ECharts.vue';
import 'echarts/lib/chart/scatter';
import 'echarts/lib/chart/line';
import 'echarts/lib/component/tooltip';
import 'echarts/lib/component/title';
import 'echarts/lib/component/dataZoom';
import ecStats from 'echarts-stat';

function titleCase(str) {
  const str1 = str
    .trim()
    .split(' ')
    .map((w) => {
      if (!w) {
        return '';
      }
      return w[0].toUpperCase() + w.substr(1).toLowerCase();
    })
    .filter((w) => w)
    .join(' ');
  if (!str1) {
    return '';
  }
  return str1
    .split('/')
    .map((w) => w[0].toUpperCase() + w.substr(1))
    .join('/');
}

Vue.component('chart', ECharts);
export default Vue.extend({
  props: {
    companyOnHover: {
      type: String,
    },
    companyBenefits: {
      type: Array,
    },
    height: {
      type: String,
    },
    exchangeRate: Object,
  },
  watch: {
    companyOnHover() {
      this.$refs.echartInstance.dispatchAction({
        type: 'downplay',
        seriesIndex: 0,
      });
      if (this.companyOnHover !== '') {
        const indices = [];
        this.revCompen.forEach((c, i) => {
          if (c.name.startsWith(this.companyOnHover)) {
            indices.push(i);
          }
        });
        for (let index = 0; index < indices.length; index += 1) {
          const i = indices[index];
          this.$refs.echartInstance.dispatchAction({
            type: 'highlight',
            seriesIndex: 0,
            dataIndex: i,
          });
        }
      }
    },
  },
  computed: {
    revCompen() {
      const revCompen = [];
      this.companyBenefits.forEach((company) => {
        company.positions.forEach((pos) => {
          const ben = pos[this.benefitType.value];
          if (ben) {
            revCompen.push({
              name: `${company.company} (${pos.title ? titleCase(pos.title) : pos.jobGroup[0]})`,
              value: [
                company[this.against] * this.exchangeRate.usdDivisor,
                Math.round(ben * this.exchangeRate.usdDivisor),
              ],
            });
          }
        });
      });
      return revCompen;
    },
    options() {
      let regressed = [];
      regressed = ecStats.regression(
        'exponential',
        this.revCompen.map((r) => r.value),
      );
      regressed.points.sort((a, b) => a[0] - b[0]);
      regressed.points = regressed.points.map((a) => [a[0], Math.round(a[1])]);
      if (this.revCompen.length === 0) {
        return {
          title: {
            text: `${this.benefitType.text} against ${titleCase(this.against)}`,
            left: 'center',
            top: 8,
          },
          xAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                type: 'dashed',
              },
            },
          },
          yAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                type: 'dashed',
              },
            },
          },
          series: [
            {
              name: 'Actual',
              type: 'scatter',
              showSymbol: true,
              data: this.revCompen,
            },
          ],
        };
      }
      return {
        dataZoom: [
          {
            show: true,
            start: 0,
            end: 100,
          },
          {
            type: 'inside',
            start: 0,
            end: 100,
          },
        ],
        title: {
          text: `${this.benefitType.text} against ${titleCase(this.against)}`,
          left: 'center',
          top: 8,
        },
        tooltip: {
          trigger: 'item',
          axisPointer: {
            type: 'cross',
          },
        },
        xAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              type: 'dashed',
            },
          },
        },
        yAxis: {
          type: 'value',
          splitLine: {
            lineStyle: {
              type: 'dashed',
            },
          },
        },
        series: [
          {
            name: 'Actual',
            type: 'scatter',
            showSymbol: true,
            data: this.revCompen,
          },
          {
            name: 'Regressed',
            type: 'line',
            smooth: true,
            data: regressed.points,
            showSymbol: false,
            lineStyle: {
              normal: {
                width: 0.3,
                type: 'dotted',
              },
            },
          },
        ],
        legend: {
          data: ['Actual', 'Regressed'],
          right: 10,
          top: '10%',
          width: 120,
        },
        animationDuration: 500,
      };
    },
  },
  data() {
    return {
      allBenefitTypes: [
        {
          value: 'salaryUSD',
          text: 'C1: Annual Basic Salary',
        },
        // eslint-disable-next-line
        {
          value: 'allowanceUSD',
          text: 'Fixed Allowance',
          tip: 'Excluding perquisites such as Car allowance in lieu of car',
        },
        {
          value: 'fixedCashUSD',
          text: 'C2: Fixed Cash',
        },
        {
          value: 'shortTermIncentiveUSD',
          text: 'Annual Variable Cash',
          tip: 'Includes performance bonus, commission',
        },
        {
          value: 'totalCashUSD',
          text: 'C3: Total Cash',
        },
        {
          value: 'longTermIncentiveUSD',
          text: 'Long-term Incentive',
          tip: 'Eg. equity, stock plan, Long-term cash',
        },
        {
          value: 'totalDirectCompensationUSD',
          text: 'C4: Total Direct Compensation',
        },
        {
          value: 'totalPerquisitesUSD',
          text: 'Perquisites',
          tip: "Eg. Benefits such as Value of Car, Children's education, Housing",
        },
        {
          value: 'totalRemunerationUSD',
          text: 'C5: Total Renumeration',
        },
        {
          value: 'totalSocialBenefitsUSD',
          text: 'Pension',
          tip: 'Mandatory such as CPF or supplementary',
        },
        {
          value: 'totalCostUSD',
          text: 'C6: Total Cost to Company',
        },
        {
          value: 'directorFeeUSD',
          text: 'Director Fee',
        },
      ],
      benefitType: {
        value: 'totalCostUSD',
        text: 'C6: Total Cost to Company',
      },
      against: 'revenue',
    };
  },
  components: {
    ECharts,
  },
});
</script>
<style scoped>
.echarts {
  /* height: 100%; */
  width: 100%;
}

.topright {
  position: absolute;
  right: 0px;
  width: 240px;
  z-index: 2;
}

.topleft {
  position: absolute;
  left: 20px;
  width: 260px;
  z-index: 2;
}
</style>
